import { Box, FormControlLabel, SvgIcon, Switch, Typography } from '@mui/material';
import { AgentPermissionGate, AgentPermissionProvider } from '@opengov/agents-browser-sdk';
import { Check as CheckIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { useNavAuth } from '../../ContextSwitcher/Context/NavAuthProvider';
import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { useEntity } from '../../OgAssist/Context/EntityContext';
import { useNavBarProps } from '../../TopLevelNavigation/NavBarPropsContext';
import { ISwitchMenuItem } from '../../types';
import { handleTabOrEscapeToLeaveSubmenu } from '../../utils/keyboardNavUtils';

const getNextFocusableElementIndex = (currentIndex: number, direction: string, elementsLength: number) => {
  let nextIndex = direction === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1;
  if (nextIndex < 0) {
    nextIndex = elementsLength - 1; // cycle to the last element
  } else if (nextIndex >= elementsLength) {
    nextIndex = 0; // cycle to the first element
  }
  return nextIndex;
};

export const arrowUpDownKeyboardFocusHandler = (keyboardEvent: React.KeyboardEvent, container: Element | null) => {
  if (!container) {
    console.error('Container element is not found');
    return;
  }
  const { key } = keyboardEvent;
  if (key !== 'ArrowDown' && key !== 'ArrowUp') return;
  keyboardEvent.preventDefault();
  keyboardEvent.stopPropagation();
  // Gather all switch (input) elements in the container, and menu item (li) elements if any (for mobile)
  const focusableElements = Array.from(container.querySelectorAll('input, li'));
  const currentIndex = focusableElements.indexOf(document.activeElement as HTMLInputElement);
  const nextIndex = getNextFocusableElementIndex(currentIndex, key, focusableElements.length);
  (focusableElements[nextIndex] as HTMLElement).focus();
};

interface ISwitchComponentProps extends ISwitchMenuItem {
  autoFocus: boolean;
  parentContainerRef: React.RefObject<HTMLUListElement>;
  firstSwitchInputRef?: React.MutableRefObject<HTMLInputElement> | null;
  anchorEl?: HTMLElement | null;
}

interface IRenderSwitchMenuItemProps {
  title: string;
  description: string;
  switchLabel?: string;
  dataOG: string;
  autoFocus: boolean;
  anchorEl: HTMLElement | null;
  parentContainerRef: React.RefObject<HTMLUListElement>;
  firstSwitchInputRef?: React.MutableRefObject<HTMLInputElement> | null;
  switchValue: boolean;
  onSwitchValueChange: (newValue: boolean) => Promise<void>;
  onSwitchToggle: () => void;
}

const renderSwitchMenuItem = ({
  title,
  description,
  switchLabel,
  dataOG,
  autoFocus,
  anchorEl,
  parentContainerRef,
  firstSwitchInputRef,
  switchValue,
  onSwitchValueChange,
  onSwitchToggle
}: IRenderSwitchMenuItemProps) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      p={1}
      className="switch_menu_item"
      data-test={getDynamicDataTestId(DATA_TEST.switch_menu_item_container, title)}
      sx={{
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        ':last-child': {
          borderBottom: 'none'
        }
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography id={`switch-title-${title}`} variant="h5" component="h3" role="presentation">
          {title}
        </Typography>
        <FormControlLabel
          sx={{ m: 0, pl: 3 }}
          data-og={getDynamicDataOGId(DATA_OG.switch_menu_item, dataOG)}
          control={
            <Switch
              inputProps={{
                role: 'menuitemcheckbox',
                'aria-checked': switchValue,
                'aria-labelledby': `switch-title-${title}`,
                'aria-describedby': `switch-description-${description}`
              }}
              autoFocus={autoFocus}
              inputRef={firstSwitchInputRef}
              data-test={getDynamicDataTestId(DATA_TEST.switch_menu_item, title)}
              size="small"
              onKeyDown={async (keyboardEvent) => {
                if (['ArrowDown', 'ArrowUp'].includes(keyboardEvent.key)) {
                  arrowUpDownKeyboardFocusHandler(keyboardEvent, parentContainerRef?.current);
                }
                handleTabOrEscapeToLeaveSubmenu(keyboardEvent.key, anchorEl);
                if (['Enter'].includes(keyboardEvent.key)) {
                  await onSwitchValueChange(!switchValue);
                  onSwitchToggle();
                }
                if ([' '].includes(keyboardEvent.key)) {
                  await onSwitchValueChange(!switchValue);
                }
              }}
              checkedIcon={
                <span className="MuiSwitch-thumb">
                  <SvgIcon inheritViewBox component={CheckIcon} fontSize="inherit" />
                </span>
              }
              checked={switchValue}
              onChange={async () => {
                await onSwitchValueChange(!switchValue);
                onSwitchToggle();
              }}
            />
          }
          label={<Typography variant="bodySmall">{switchLabel || 'On'}</Typography>}
          labelPlacement="end"
        />
      </Box>
      <Typography id={`switch-description-${description}`} variant="bodySmall" component="p">
        {description}
      </Typography>
    </Box>
  );
};

export const SwitchMenuItem: React.FC<ISwitchComponentProps> = ({
  title,
  description,
  value,
  switchLabel,
  dataOG = '',
  autoFocus,
  anchorEl = null,
  parentContainerRef,
  firstSwitchInputRef,
  requiresOgAssistPermission = false,
  onValueChange
}) => {
  const [switchValue, setSwitchValue] = React.useState(value);

  const { entityUUID } = useEntity();
  const { authBearerToken } = useNavAuth();
  const { unstable_ogAgentOptions } = useNavBarProps();

  const handleSwitchToggle = () => {
    setSwitchValue((v) => !v);
  };

  const switchMenuItemContent = renderSwitchMenuItem({
    title,
    description,
    switchLabel,
    dataOG,
    autoFocus,
    anchorEl,
    parentContainerRef,
    firstSwitchInputRef,
    switchValue,
    onSwitchValueChange: onValueChange,
    onSwitchToggle: handleSwitchToggle
  });
  // Most cases: simply return the switch menu item content
  if (!requiresOgAssistPermission) return switchMenuItemContent;

  // Otherwise if they require og assist permission, only render if the user has the OG Assist permission
  const { agentsBaseUrl } = unstable_ogAgentOptions ?? {};
  return (
    <AgentPermissionProvider entityId={entityUUID} authToken={authBearerToken} baseURL={agentsBaseUrl}>
      <AgentPermissionGate>{switchMenuItemContent}</AgentPermissionGate>
    </AgentPermissionProvider>
  );
};
